import { AxiosResponse } from "axios";
import { call, fork, put, takeLatest } from "redux-saga/effects";

import {
  CalibrationPayload,
  CreateDevicePayload,
  DeviceItem,
  DeviceListResponse,
  DeviceProfileListResponse,
  DeviceStatistics,
  EventListResponse,
  MetricListResponse,
  UpdateDevicePayload,
} from "entities/devices";
import { DevicesApi } from "services/http";
import { showToast } from "store/slices/toastSlice";
import { getApiErrorMessage, isFieldLevelError } from "utils/helpers";
import {
  createDeviceFailure,
  createDeviceRequest,
  createDeviceSuccess,
  markCalibrationFailure,
  markCalibrationRequest,
  markCalibrationSuccess,
  getDeviceEventsFailure,
  getDeviceEventsRequest,
  getDeviceEventsSuccess,
  getDeviceFailure,
  getDeviceListFailure,
  getDeviceListRequest,
  getDeviceListSuccess,
  getDeviceMetricsFailure,
  getDeviceMetricsRequest,
  getDeviceMetricsSuccess,
  getDeviceProfilesFailure,
  getDeviceProfilesRequest,
  getDeviceProfilesSuccess,
  getDeviceRequest,
  getDeviceStatisticsFailure,
  getDeviceStatisticsRequest,
  getDeviceStatisticsSuccess,
  getDeviceSuccess,
  removeDeviceFailure,
  removeDeviceRequest,
  removeDeviceSuccess,
  updateDeviceFailure,
  updateDeviceRequest,
  updateDeviceSuccess,
} from "store/slices/deviceSlice";

function* getListFlow(action: { type: string; payload: string }) {
  try {
    const response: AxiosResponse<DeviceListResponse> = yield call(
      DevicesApi.getList,
      action.payload,
    );
    yield put(getDeviceListSuccess(response.data));
  } catch {
    yield put(getDeviceListFailure());
  }
}

function* getStatisticsFlow(action: { type: string; payload: string | undefined }) {
  try {
    const response: AxiosResponse<DeviceStatistics> = yield call(
      DevicesApi.getStatistics,
      action.payload ?? "",
    );
    yield put(getDeviceStatisticsSuccess(response.data));
  } catch {
    yield put(getDeviceStatisticsFailure());
  }
}

function* getCurrentFlow(action: { type: string; payload: string }) {
  try {
    const response: AxiosResponse<DeviceItem> = yield call(DevicesApi.getCurrent, action.payload);
    yield put(getDeviceSuccess(response.data));
  } catch {
    yield put(getDeviceFailure());
  }
}

function* getProfilesFlow() {
  try {
    const response: AxiosResponse<DeviceProfileListResponse> = yield call(DevicesApi.getProfiles);
    yield put(getDeviceProfilesSuccess(response.data.items));
  } catch {
    yield put(getDeviceProfilesFailure());
  }
}

function* getMetricsFlow(action: {
  type: string;
  payload: { devEui: string; query: string };
}) {
  try {
    const response: AxiosResponse<MetricListResponse> = yield call(
      DevicesApi.getMetrics,
      action.payload.devEui,
      action.payload.query,
    );
    yield put(getDeviceMetricsSuccess(response.data));
  } catch {
    yield put(getDeviceMetricsFailure());
  }
}

function* getEventsFlow(action: {
  type: string;
  payload: { devEui: string; query: string };
}) {
  try {
    const response: AxiosResponse<EventListResponse> = yield call(
      DevicesApi.getEvents,
      action.payload.devEui,
      action.payload.query,
    );
    yield put(getDeviceEventsSuccess(response.data));
  } catch {
    yield put(getDeviceEventsFailure());
  }
}

function* watchGetList() {
  yield takeLatest(getDeviceListRequest.type, getListFlow);
}

function* watchGetStatistics() {
  yield takeLatest(getDeviceStatisticsRequest.type, getStatisticsFlow);
}

function* watchGetCurrent() {
  yield takeLatest(getDeviceRequest.type, getCurrentFlow);
}

function* watchGetProfiles() {
  yield takeLatest(getDeviceProfilesRequest.type, getProfilesFlow);
}

function* watchGetMetrics() {
  yield takeLatest(getDeviceMetricsRequest.type, getMetricsFlow);
}

function* watchGetEvents() {
  yield takeLatest(getDeviceEventsRequest.type, getEventsFlow);
}

function* createDeviceFlow(action: {
  type: string;
  payload: { payload: CreateDevicePayload; certificate: File | null };
}) {
  try {
    const response: AxiosResponse<DeviceItem> = yield call(
      DevicesApi.create,
      action.payload.payload,
    );
    const device = response.data;
    if (action.payload.certificate) {
      try {
        yield call(
          DevicesApi.uploadCertificate,
          device.dev_eui,
          action.payload.certificate,
        );
      } catch {
      }
    }
    yield put(createDeviceSuccess(device));
    yield put(
      showToast({
        id: `device-created-${device.id}`,
        message: device.asset_name
          ? `The device was successfully added to ${device.asset_name}.`
          : "The device was successfully added.",
        type: "success",
      }),
    );
  } catch (err: unknown) {
    const detail = (err as { detail?: unknown } | null)?.detail;
    let msg = "Failed to create device";
    if (typeof detail === "string") {
      msg = detail;
    } else if (detail && typeof detail === "object") {
      const values = Object.values(detail as Record<string, unknown>);
      if (values.length && typeof values[0] === "string") {
        msg = values[0] as string;
      }
    }
    yield put(createDeviceFailure(msg));

    if (!isFieldLevelError(err)) {
      yield put(
        showToast({
          id: `device-create-failed-${Date.now()}`,
          message: getApiErrorMessage(err, "Failed to add device. Please try again later."),
          type: "error",
        }),
      );
    }
  }
}

function* watchCreateDevice() {
  yield takeLatest(createDeviceRequest.type, createDeviceFlow);
}

function* updateDeviceFlow(action: {
  type: string;
  payload: { devEui: string; body: UpdateDevicePayload };
}) {
  const { devEui, body } = action.payload;
  try {
    const response: AxiosResponse<DeviceItem> = yield call(
      DevicesApi.update,
      devEui,
      body,
    );
    const device = response.data;
    yield put(updateDeviceSuccess(device));
    yield put(
      showToast({
        id: `device-updated-${device.id}`,
        message: "The device was successfully updated.",
        type: "success",
      }),
    );
  } catch (err: unknown) {
    const detail = (err as { detail?: unknown } | null)?.detail;
    let msg = "Failed to update device";
    if (typeof detail === "string") {
      msg = detail;
    } else if (detail && typeof detail === "object") {
      const values = Object.values(detail as Record<string, unknown>);
      if (values.length && typeof values[0] === "string") {
        msg = values[0] as string;
      }
    }
    yield put(updateDeviceFailure(msg));

    if (!isFieldLevelError(err)) {
      yield put(
        showToast({
          id: `device-update-failed-${Date.now()}`,
          message: getApiErrorMessage(err, "Failed to update device. Please try again later."),
          type: "error",
        }),
      );
    }
  }
}

function* watchUpdateDevice() {
  yield takeLatest(updateDeviceRequest.type, updateDeviceFlow);
}

function* removeDeviceFlow(action: { type: string; payload: string }) {
  const devEui = action.payload;
  try {
    yield call(DevicesApi.remove, devEui);
    yield put(removeDeviceSuccess(devEui));
    yield put(
      showToast({
        id: `device-removed-${devEui}-${Date.now()}`,
        message: "The device was successfully deleted.",
        type: "success",
      }),
    );
  } catch (err: unknown) {
    const detail = (err as { detail?: unknown } | null)?.detail;
    let msg = "Failed to delete device";
    if (typeof detail === "string") {
      msg = detail;
    } else if (detail && typeof detail === "object") {
      const values = Object.values(detail as Record<string, unknown>);
      if (values.length && typeof values[0] === "string") {
        msg = values[0] as string;
      }
    }
    yield put(removeDeviceFailure(msg));

    if (!isFieldLevelError(err)) {
      yield put(
        showToast({
          id: `device-remove-failed-${Date.now()}`,
          message: "Failed to delete device. Please try again later.",
          type: "error",
        }),
      );
    }
  }
}

function* watchRemoveDevice() {
  yield takeLatest(removeDeviceRequest.type, removeDeviceFlow);
}

function* markCalibrationFlow(action: {
  type: string;
  payload: { devEui: string; values: CalibrationPayload };
}) {
  const { devEui, values } = action.payload;
  try {
    const response: AxiosResponse<DeviceItem> = yield call(
      DevicesApi.markCalibration,
      devEui,
      values,
    );
    yield put(markCalibrationSuccess(response.data));
    yield put(
      showToast({
        id: `device-calibrated-${devEui}-${Date.now()}`,
        message: "Calibration record saved successfully.",
        type: "success",
      }),
    );
  } catch (err: unknown) {
    const detail = (err as { detail?: unknown } | null)?.detail;
    let msg = "Failed to save calibration record";
    if (typeof detail === "string") {
      msg = detail;
    } else if (detail && typeof detail === "object") {
      const values = Object.values(detail as Record<string, unknown>);
      if (values.length && typeof values[0] === "string") {
        msg = values[0] as string;
      }
    }
    yield put(markCalibrationFailure(msg));

    if (!isFieldLevelError(err)) {
      yield put(
        showToast({
          id: `device-calibrate-failed-${Date.now()}`,
          message: getApiErrorMessage(err, "Failed to save calibration record. Please try again later."),
          type: "error",
        }),
      );
    }
  }
}

function* watchMarkCalibration() {
  yield takeLatest(markCalibrationRequest.type, markCalibrationFlow);
}

export const deviceWatchers = [
  fork(watchGetList),
  fork(watchGetStatistics),
  fork(watchGetCurrent),
  fork(watchGetProfiles),
  fork(watchGetMetrics),
  fork(watchGetEvents),
  fork(watchCreateDevice),
  fork(watchUpdateDevice),
  fork(watchRemoveDevice),
  fork(watchMarkCalibration),
];
